import React, { useState, useCallback, useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';
import useSWR from 'swr';
import {
  AgntMeritTags,
  GetShopStatisticResponse,
  GetShopDetailReviewResponse,
} from '@remodeling/goods';
import { shopDetailGateway } from '@/di-container/shop/detail/gateway/client';
import { getAgntEmoji } from '@/utils/emoji';

type ShopSortType = 'RECOMMENDED' | 'LATEST';

interface useShopDetailCustomerReviewProps {
  sldToCd: string;
  ssrData?: {
    statisticData?: GetShopStatisticResponse | null;
    reviewData?: GetShopDetailReviewResponse | null;
  } | null;
}

const PAGE_SIZE = 4;

const useShopDetailCustomerReview = ({
  sldToCd,
  ssrData,
}: useShopDetailCustomerReviewProps) => {
  const { getShopStatistics, getShopDetailReview } = shopDetailGateway();
  const [sortType, setSortType] = useState<ShopSortType>('RECOMMENDED');

  const { data: statisticData } = useSWR<GetShopStatisticResponse | null>(
    sldToCd ? `shop-statistic-${sldToCd}` : null,
    () => getShopStatistics.exec({ sldToCd }).then((res) => res ?? null),
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );

  const getKey = (
    pageIndex: number,
    previousPageData: GetShopDetailReviewResponse | null,
  ) => {
    if (!sldToCd) return null;

    if (previousPageData?.data?.content?.length === 0) return null;

    const nextPage = pageIndex + 1;

    return {
      sldToCd,
      typeValue: 'WEB',
      page: nextPage,
      size: PAGE_SIZE,
      sort: sortType,
    };
  };

  const { data: reviewPages, setSize } =
    useSWRInfinite<GetShopDetailReviewResponse | null>(
      getKey,
      (params) => getShopDetailReview.exec(params),
      {
        revalidateOnFocus: false,
        shouldRetryOnError: false,
        fallbackData: ssrData?.reviewData ? [ssrData.reviewData] : undefined,
        revalidateIfStale: false,
        revalidateFirstPage: false,
        persistSize: true,
      },
    );

  const reviews = useMemo(() => {
    return reviewPages?.flatMap((page) => page?.data?.content ?? []) ?? [];
  }, [reviewPages]);

  const totalElements = reviewPages?.[0]?.data?.totalElements ?? 0;
  const isLastPage = reviewPages?.[reviewPages.length - 1]?.data?.last ?? true;

  const gdsMeritTagsWithEmoji = useMemo(
    () =>
      statisticData?.data?.meritTagCounts?.map((tag) => ({
        ...tag,
        emoji: getAgntEmoji(tag.name as AgntMeritTags),
      })) ?? [],
    [statisticData?.data?.meritTagCounts],
  );

  const handleClickMore = useCallback(() => {
    if (!isLastPage) {
      setSize((prev) => prev + 1);
    }
  }, [isLastPage, setSize]);

  const handleChangeSort = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const value = e.currentTarget.dataset.value as ShopSortType;
      if (value === 'RECOMMENDED' || value === 'LATEST') {
        setSortType(value);
        setSize(1);
      }
    },
    [setSize],
  );

  const hasRating = useMemo(
    () => (statisticData?.data?.shopStatistics?.totAvgScore ?? 0) > 0,
    [statisticData?.data?.shopStatistics?.totAvgScore],
  );

  const hasMeritTags = useMemo(
    () => gdsMeritTagsWithEmoji.length > 0,
    [gdsMeritTagsWithEmoji],
  );

  return {
    statistics: statisticData?.data ?? null,
    reviews,
    isLastPage,
    totalElements,
    sortType,
    handleClickMore,
    handleChangeSort,
    gdsMeritTagsWithEmoji,
    hasRating,
    hasMeritTags,
  };
};

export default useShopDetailCustomerReview;
